/**
 * @file One transfer, in flight or failed, rendered in place of the file row it
 * concerns. Consumer: `AttachmentList`, which renders it for a file that has a
 * transfer and above the list for a transfer whose file has no row yet.
 *
 * This component owns the subscription to the transfer's own leaves, which is
 * the point of the split: a progress tick mutates `bytesTransferred` on one
 * transfer, and a failure mutates `failure` on one transfer, so only that row
 * re-renders — never the list, never a sibling row. Reading
 * `snap.transfers[transferId]` and nothing wider is what keeps that true, so do
 * not lift these reads into the list.
 *
 * Both controls are always rendered and disabled when the state cannot honour
 * them, because which primitive a file took is invisible to the reader and two
 * uploads side by side must not offer visibly different controls; the tooltip
 * says what cannot be done instead. Cancel has two disabled reasons, not one:
 * `pending` is the reservation window of an upload that will offer Cancel in a
 * moment, and `never` is a primitive that can never be stopped. Both name
 * sending, because an assembly registers its `AbortController` in the same tick
 * it opens its row and so is only ever seen `ready`. A failed row keeps the
 * file's place until the reader retries or dismisses it, which is the same
 * control that cancels an in-flight one: both mean stop showing me this, and the
 * word has to match what happens.
 */
import { FC } from 'react';
import { Button, Tooltip, Typography } from 'antd';
import { useSnapshot } from 'valtio';
import FlexBox from '../../atoms/FlexBox';
import FlexCol from '../../atoms/FlexCol';
import {
    attachmentTransfersActions,
    attachmentTransfersState,
} from '../../../store/attachmentTransfersStore';
import { resolveAttachmentTransferCaption } from './resolveAttachmentTransferCaption';

const { Text } = Typography;

interface AttachmentTransferRowProps {
    /** Which transfer to render; its absence from the store renders nothing. */
    transferId: string;
}

export const AttachmentTransferRow: FC<AttachmentTransferRowProps> = ({ transferId }) => {
    const snap = useSnapshot(attachmentTransfersState);
    const transfer = snap.transfers[transferId];

    if (!transfer) {
        return null;
    }

    const caption = resolveAttachmentTransferCaption(transfer);
    const failed = transfer.failure !== null;
    const movement = transfer.kind === 'assemble' ? 'download' : 'upload';
    const cancelBlockedReason = failed || transfer.cancellation === 'ready'
        ? null
        : transfer.cancellation === 'pending'
            ? 'This upload is still starting. Cancel becomes available once it begins sending.'
            : 'This upload cannot be stopped once it has started.';
    const retryBlockedReason = failed
        ? null
        : `This ${movement} has not failed, so there is nothing to retry.`;
    const stopLabel = failed ? 'Dismiss' : 'Cancel';

    return (
        <FlexBox
            stretch
            style={{
                padding: 5,
                alignItems: 'center',
                width: '100%',
            }}
            id={`attachment-transfer-${transferId}`}
        >
            <Text
                style={{
                    width: 300,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                }}
            >
                {transfer.fileName}
            </Text>
            <FlexCol gap={0} noGrow alignStart>
                <Text type={failed ? 'danger' : undefined}>{caption.headline}</Text>
                <Text type="secondary">{caption.detail}</Text>
            </FlexCol>
            <Tooltip title={retryBlockedReason ?? `Retry ${transfer.fileName}`}>
                <span style={{ display: 'inline-flex' }}>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => attachmentTransfersActions.retryTransfer(transferId)}
                        disabled={retryBlockedReason !== null}
                        id={`attachment-transfer-retry-${transferId}`}
                        aria-label={`Retry ${transfer.fileName}`}
                    >
                        Retry
                    </Button>
                </span>
            </Tooltip>
            <Tooltip title={cancelBlockedReason ?? `${stopLabel} ${transfer.fileName}`}>
                <span style={{ display: 'inline-flex' }}>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => (failed
                            ? attachmentTransfersActions.endTransfer(transferId)
                            : attachmentTransfersActions.cancelTransfer(transferId))}
                        disabled={cancelBlockedReason !== null}
                        id={`attachment-transfer-${failed ? 'dismiss' : 'cancel'}-${transferId}`}
                        aria-label={`${stopLabel} ${transfer.fileName}`}
                    >
                        {stopLabel}
                    </Button>
                </span>
            </Tooltip>
        </FlexBox>
    );
};
